/**
 * whyChooseReasons.ts
 *
 * City-specific and general "Why Choose Danis Painting" reasons.
 * Each service+city page picks 6 reasons deterministically:
 *   - 2–3 city-specific reasons (unique local knowledge)
 *   - 3–4 general Danis differentiators (rotated per combo)
 * This ensures every page has genuinely localized content while
 * still communicating core company strengths.
 */

export interface WhyChooseReason {
  headline: string;
  detail: string;
}

/**
 * City-specific reasons — each city has 4–6 unique items referencing
 * local landmarks, climate, architecture, regulations, and community.
 */
export const CITY_REASONS: Record<string, WhyChooseReason[]> = {
  "santa-barbara": [
    {
      headline: "ABR Color Compliance Experts",
      detail: "Santa Barbara's Architectural Board of Review enforces strict color palettes for the downtown core and many residential zones. We've navigated ABR approvals on hundreds of projects and know exactly which earth tones and Mediterranean palettes pass review.",
    },
    {
      headline: "State Street & Funk Zone Specialists",
      detail: "We've painted storefronts, tasting rooms, and restaurants throughout State Street and the Funk Zone — scheduling work around tourist foot traffic and coordinating with neighboring businesses to minimize disruption.",
    },
    {
      headline: "Riviera & Mesa Hillside Access",
      detail: "Santa Barbara's hillside neighborhoods require specialized scaffolding and access planning. Our crews have decades of experience painting multi-level homes on steep lots throughout the Riviera, Mesa, and Eastside.",
    },
    {
      headline: "Best of Santa Barbara 2025 Winner",
      detail: "Voted Best Painting Contractor in the 2025 Santa Barbara Independent Readers' Poll — recognition earned through consistent quality across thousands of local projects over three decades.",
    },
    {
      headline: "Spanish Colonial & Mediterranean Revival Expertise",
      detail: "Santa Barbara's signature architecture demands painters who understand stucco textures, clay tile rooflines, and period-appropriate color palettes. We've restored and repainted hundreds of Santa Barbara's most iconic homes.",
    },
  ],
  "ventura": [
    {
      headline: "Downtown Main Street Business Specialists",
      detail: "Ventura's revitalized Downtown Main Street corridor has strict aesthetic standards for commercial properties. We've painted dozens of storefronts, restaurants, and offices along Main Street, working evenings to avoid disrupting daytime business.",
    },
    {
      headline: "Ventura Harbor & Waterfront Experience",
      detail: "Properties near Ventura Harbor face accelerated paint degradation from salt spray and marine fog. We use marine-grade elastomeric coatings specifically formulated for the harbor's corrosive coastal environment.",
    },
    {
      headline: "Pacific View Mall Area Retail Painting",
      detail: "We've completed painting projects for retail centers and commercial properties throughout the Pacific View Mall corridor — Ventura's busiest commercial district — with zero business disruption.",
    },
    {
      headline: "Ventura's Craftsman & Victorian Home Experts",
      detail: "Ventura's historic Midtown and Downtown neighborhoods feature Craftsman bungalows and Victorian homes that require period-appropriate colors and careful trim detailing. We specialize in these architectural styles.",
    },
    {
      headline: "Ventura County's Coastal Climate Knowledge",
      detail: "Ventura's unique position where the coastline turns east-west creates intense afternoon sun exposure combined with morning marine fog. We select coatings that handle this specific UV and moisture cycling.",
    },
  ],
  "montecito": [
    {
      headline: "Luxury Estate & Mediterranean Villa Specialists",
      detail: "Montecito's multi-million-dollar estates along Hot Springs Road, East Valley Road, and Butterfly Beach demand flawless execution. We provide the meticulous craftsmanship and premium materials that Montecito homeowners expect.",
    },
    {
      headline: "Discreet Service for High-Profile Clients",
      detail: "Many Montecito properties belong to high-profile individuals who value privacy. Our crews sign NDAs when required, arrive in unmarked vehicles, and maintain complete discretion throughout every project.",
    },
    {
      headline: "Coast Village Road Commercial Experience",
      detail: "We've painted boutiques, galleries, and restaurants along Coast Village Road — Montecito's premier commercial corridor — coordinating schedules around the community's expectations for quiet, unobtrusive work.",
    },
    {
      headline: "Montecito Fire Rebuild Painting Expertise",
      detail: "After the Thomas Fire and debris flow, we painted dozens of rebuilt Montecito homes. We understand the new construction materials, fire-resistant coating requirements, and the community's rebuilding standards.",
    },
    {
      headline: "Premium Finishes for Montecito Interiors",
      detail: "Montecito's luxury interiors call for specialty finishes — Venetian plaster, lime wash, custom glazes, and hand-applied textures. We deliver the artisan-quality finishes that complement high-end interior design.",
    },
  ],
  "goleta": [
    {
      headline: "UCSB Campus & Student Housing Specialists",
      detail: "We're a preferred painting contractor for property managers near UCSB, delivering rapid-turnaround apartment repaints between lease cycles. Our crews can repaint a full unit in a single day.",
    },
    {
      headline: "Camino Real Marketplace Area Experience",
      detail: "We've painted commercial properties throughout the Camino Real Marketplace and Hollister Avenue corridor — Goleta's primary commercial district — including retail, restaurant, and office spaces.",
    },
    {
      headline: "Goleta Airport District Industrial Painting",
      detail: "The Airport District's light industrial and tech office buildings require commercial-grade coatings and flexible scheduling. We've painted facilities for aerospace, biotech, and technology companies throughout the district.",
    },
    {
      headline: "Old Town Goleta Historic Properties",
      detail: "Goleta's older neighborhoods near Hollister and Fairview feature mid-century and ranch-style homes with unique painting requirements. We know the substrates, common issues, and best products for these properties.",
    },
  ],
  "carpinteria": [
    {
      headline: "Linden Avenue Business District Specialists",
      detail: "Carpinteria's charming Linden Avenue downtown has specific aesthetic expectations. We've painted storefronts, restaurants, and commercial buildings throughout the district, maintaining the small-town character.",
    },
    {
      headline: "Direct Ocean Exposure Coating Expertise",
      detail: "Carpinteria's beachfront properties — some within 200 feet of the Pacific — face the most aggressive salt air exposure in Santa Barbara County. We use marine-grade coatings rated for direct coastal environments.",
    },
    {
      headline: "Carpinteria Bluffs & Beach Neighborhood Experience",
      detail: "From the Bluffs to the Sand Point Road neighborhood, we've painted homes throughout Carpinteria's most desirable coastal areas, understanding the specific moisture and UV challenges each microclimate presents.",
    },
    {
      headline: "Avocado Festival Town Charm",
      detail: "Carpinteria's identity as 'The World's Safest Beach' and home of the Avocado Festival reflects a community that values quality and character. We approach every Carpinteria project with that same care.",
    },
  ],
  "ojai": [
    {
      headline: "Ojai Valley Heat-Resistant Coatings",
      detail: "Ojai's inland valley location produces summer temperatures exceeding 100°F — far hotter than the coast. We select heat-resistant, UV-stable coatings specifically formulated for Ojai's extreme thermal cycling.",
    },
    {
      headline: "Downtown Arcade & Ojai Avenue Specialists",
      detail: "Ojai's iconic Spanish Colonial arcade and the boutiques along Ojai Avenue require painting that respects the city's carefully maintained architectural character and the community's artistic sensibility.",
    },
    {
      headline: "Boutique Hotel & Wellness Retreat Experience",
      detail: "Ojai's hospitality industry — from the Ojai Valley Inn to boutique retreats — demands premium finishes and zero-disruption scheduling. We've painted multiple hospitality properties throughout the valley.",
    },
    {
      headline: "Artist Colony Aesthetic Understanding",
      detail: "Ojai's identity as an artist colony means homeowners and business owners care deeply about color, texture, and finish quality. We bring the same artistic sensibility to every Ojai painting project.",
    },
  ],
  "camarillo": [
    {
      headline: "HOA Color Compliance Specialists",
      detail: "Camarillo's planned communities — Mission Oaks, Spanish Hills, Camarillo Heights — have strict HOA color palettes. We've worked with dozens of Camarillo HOAs and know the approval process inside and out.",
    },
    {
      headline: "Premium Outlets & Las Posas Road Commercial",
      detail: "We've painted commercial properties throughout the Premium Outlets area and Las Posas Road corridor, including retail, medical offices, and professional buildings in Camarillo's busiest commercial zones.",
    },
    {
      headline: "Old Town Camarillo Historic Properties",
      detail: "Old Town Camarillo's historic homes and commercial buildings along Ventura Boulevard require painting expertise that respects the area's architectural heritage while providing modern protection.",
    },
    {
      headline: "Camarillo Airport Business Park Experience",
      detail: "The Airport Business Park's industrial and office buildings require commercial-grade coatings and scheduling that works around business operations. We've painted multiple facilities in the park.",
    },
  ],
  "solvang": [
    {
      headline: "Danish Architecture Painting Specialists",
      detail: "Solvang's half-timbered facades, decorative stucco, and traditional Danish color palettes require painters who understand the specific techniques and colors that define the village's world-famous character.",
    },
    {
      headline: "Copenhagen Drive & Mission Drive Experience",
      detail: "We've painted hotels, restaurants, bakeries, and shops throughout Solvang's tourist core — scheduling work during off-peak hours to avoid disrupting the visitor experience that drives the local economy.",
    },
    {
      headline: "Santa Ynez Valley Wine Country Properties",
      detail: "Solvang sits at the gateway to Santa Ynez Valley wine country. We've painted tasting rooms, vineyard estates, and hospitality properties throughout the valley's premier wine-growing region.",
    },
    {
      headline: "Tourism Season Scheduling Expertise",
      detail: "Solvang's economy depends on year-round tourism. We schedule commercial painting projects around peak visitor seasons and events like Danish Days, ensuring zero impact on your business revenue.",
    },
  ],
  "buellton": [
    {
      headline: "Avenue of Flags Restaurant & Hospitality",
      detail: "Buellton's Avenue of Flags is home to iconic restaurants and hotels that draw Highway 101 travelers. We've painted multiple hospitality properties along this corridor, working around guest schedules.",
    },
    {
      headline: "Highway 101 Corridor Commercial Painting",
      detail: "Buellton's position on Highway 101 means commercial properties face constant vehicle exhaust, road grime, and UV exposure. We use commercial-grade coatings designed for high-traffic corridor environments.",
    },
    {
      headline: "Industrial Way Warehouse & Facility Painting",
      detail: "Buellton's Industrial Way district houses wineries, production facilities, and warehouses that require industrial-grade coatings. We have the equipment and expertise for large-scale commercial applications.",
    },
    {
      headline: "Santa Ynez Valley Climate Knowledge",
      detail: "Buellton's valley location creates hot, dry summers and wet winters with significant temperature swings. We select coatings that handle this thermal cycling without cracking or peeling.",
    },
  ],
  "santa-ynez": [
    {
      headline: "Ranch & Estate Property Specialists",
      detail: "Santa Ynez Valley's large ranch properties — many with multiple structures including main homes, guest houses, barns, and stables — require coordinated painting across diverse building types and substrates.",
    },
    {
      headline: "Sagunto Street Downtown Business Painting",
      detail: "Santa Ynez's small but charming downtown along Sagunto Street features Western-themed storefronts and restaurants. We paint these commercial properties with colors and finishes that complement the valley's character.",
    },
    {
      headline: "Wine Tasting Room & Vineyard Estate Experience",
      detail: "Santa Ynez Valley's world-class wineries demand premium finishes for their tasting rooms and estate buildings. We've painted multiple winery properties, understanding the aesthetic standards of the wine industry.",
    },
    {
      headline: "Agricultural Property Coating Expertise",
      detail: "Santa Ynez Valley's agricultural buildings — barns, equipment storage, fencing — require durable coatings that withstand dust, moisture, and UV exposure. We use agricultural-grade products for these applications.",
    },
  ],
  "isla-vista": [
    {
      headline: "Multi-Unit Rental Property Specialists",
      detail: "Isla Vista's dense student housing stock requires painters who can deliver fast, cost-effective repaints between lease cycles. We repaint full apartments in a single day with durable, washable finishes.",
    },
    {
      headline: "Preferred by IV Property Managers",
      detail: "Major Isla Vista property management companies rely on us for annual unit turns and building maintenance painting. Our volume pricing and rapid scheduling make us the most efficient choice for multi-unit portfolios.",
    },
    {
      headline: "Del Playa Drive Oceanfront Protection",
      detail: "Del Playa Drive's clifftop apartments face direct Pacific Ocean exposure — the most aggressive salt air environment in the UCSB area. We use marine-grade coatings that protect these high-value oceanfront properties.",
    },
    {
      headline: "Durable, High-Traffic Finishes",
      detail: "Student housing takes more physical wear than typical residential properties. We use commercial-grade, washable paints and scuff-resistant finishes that maintain their appearance through heavy daily use.",
    },
  ],
  "summerland": [
    {
      headline: "Historic Victorian & Craftsman Home Experts",
      detail: "Summerland's Victorian and Craftsman-era homes along Lillingston Canyon Road and Via Real require period-appropriate colors and careful restoration techniques that preserve their historic character.",
    },
    {
      headline: "Pacific Oceanfront Coastal Protection",
      detail: "Summerland's position directly on the Pacific coast exposes properties to salt spray, coastal fog, and intense UV. We use marine-grade coatings specifically formulated for direct oceanfront environments.",
    },
    {
      headline: "Antique District Commercial Painting",
      detail: "Summerland's antique shops and boutiques along Lillingston Canyon Road have specific aesthetic expectations. We paint these commercial properties with finishes that complement the community's vintage character.",
    },
    {
      headline: "Small-Town Care & Attention",
      detail: "Summerland's tight-knit community values quality and personal service. We approach every Summerland project with the care, attention, and respect that this special coastal village deserves.",
    },
  ],
  "los-angeles": [
    {
      headline: "Westside & Santa Monica Coastal Properties",
      detail: "LA's Westside neighborhoods — Santa Monica, Venice, Pacific Palisades, Malibu — face marine layer moisture and salt air that accelerate paint degradation. We use coastal-rated coatings for lasting protection.",
    },
    {
      headline: "Hollywood Hills & Silver Lake Hillside Access",
      detail: "LA's hillside neighborhoods require specialized scaffolding and access planning for multi-level homes on steep lots. Our crews have extensive experience with the unique challenges of hillside properties.",
    },
    {
      headline: "San Fernando Valley Heat-Resistant Coatings",
      detail: "The Valley's extreme summer heat — regularly exceeding 100°F — causes standard paint to blister and peel. We select heat-resistant, UV-stable coatings formulated for LA's hottest inland microclimates.",
    },
    {
      headline: "LA's Diverse Architectural Styles",
      detail: "From Spanish Colonial Revival in Los Feliz to Mid-Century Modern in the Hills to contemporary in West Hollywood — we've painted every architectural style that defines Los Angeles and know which products work best on each.",
    },
  ],
  "oxnard": [
    {
      headline: "Channel Islands Harbor Waterfront Specialists",
      detail: "Oxnard's Channel Islands Harbor properties face direct marine exposure — salt spray, fog, and UV. We use marine-grade elastomeric coatings specifically formulated for the harbor's corrosive coastal environment.",
    },
    {
      headline: "RiverPark & Financial Plaza Commercial",
      detail: "We've painted commercial properties throughout Oxnard's RiverPark development and Financial Plaza — the city's premier business districts — including offices, retail, and restaurant spaces.",
    },
    {
      headline: "Oxnard Plain Agricultural Climate Knowledge",
      detail: "Oxnard's position on the agricultural plain means properties face both coastal moisture from the west and elevated groundwater from surrounding irrigation. We select coatings that handle this high-moisture environment.",
    },
    {
      headline: "Downtown Oxnard Historic District Experience",
      detail: "Downtown Oxnard's A Street and Fifth Street corridors feature historic commercial buildings that require painting expertise respecting the area's architectural heritage while providing modern protection.",
    },
  ],
};

/**
 * General Danis differentiators — company-wide strengths that apply
 * to every city. The selection function rotates these per service+city
 * combo so no two pages show the same set.
 */
export const GENERAL_REASONS: WhyChooseReason[] = [
  // Credentials & trust
  {
    headline: "30+ Years of Proven Experience",
    detail: "Mark Danis founded this company in the early 1990s and has personally overseen thousands of projects across Santa Barbara, Ventura, and Los Angeles Counties. That depth of experience shows in every job.",
  },
  {
    headline: "California Licensed & Fully Insured",
    detail: "We carry a current California Contractor License (CSLB #662554), general liability insurance, and workers' compensation coverage on every crew member — protecting your property and giving you complete peace of mind.",
  },
  {
    headline: "Owner-Supervised Projects",
    detail: "Mark Danis personally visits every job site during the project. You're not handing your home or business to a subcontractor you've never met — you're working directly with the owner.",
  },
  {
    headline: "Honest, Transparent Pricing",
    detail: "Our written estimates break down every cost — labor, materials, prep, and cleanup. No surprise add-ons at the end. What we quote is what you pay.",
  },
  {
    headline: "Free, No-Obligation Estimates",
    detail: "We come to your property, assess the scope, and provide a detailed written estimate at no charge. There's no pressure and no commitment required.",
  },
  {
    headline: "3-Year Workmanship Warranty",
    detail: "We stand behind our craftsmanship with a written 3-year warranty covering peeling, cracking, and adhesion failures under normal conditions. If something isn't right, we fix it — no questions asked.",
  },
  // Quality & materials
  {
    headline: "Premium Sherwin-Williams & Benjamin Moore Paints",
    detail: "We use only professional-grade paints from Sherwin-Williams, Benjamin Moore, and Dunn-Edwards — never builder-grade products. Better paint means better coverage, richer color, and longer-lasting results.",
  },
  {
    headline: "Meticulous Surface Preparation",
    detail: "We spend as much time preparing surfaces as we do painting them. Proper cleaning, sanding, patching, and priming are what separate a paint job that lasts 10 years from one that peels in two.",
  },
  {
    headline: "Low-VOC & Eco-Friendly Options",
    detail: "We offer a full range of low-VOC and zero-VOC paints for clients who prioritize indoor air quality, have young children, or are pursuing LEED or green building certifications.",
  },
  {
    headline: "Color Consultation Included",
    detail: "Not sure which color is right? We provide complimentary color consultation with every project, drawing on decades of experience matching paint to architecture, lighting, and neighborhood character.",
  },
  // Process & professionalism
  {
    headline: "Detailed Written Scope of Work",
    detail: "Before we start, you receive a written scope of work listing every surface to be painted, the number of coats, the specific products to be used, and the timeline. No ambiguity, no surprises.",
  },
  {
    headline: "Clean, Respectful Job Sites",
    detail: "Our crews protect your floors, furniture, and landscaping with drop cloths and plastic sheeting. At the end of every day we clean up — and at the end of the project your property looks better than when we arrived.",
  },
  {
    headline: "On-Time Project Completion",
    detail: "We provide a realistic timeline before we start and stick to it. If weather or unforeseen conditions cause a delay, we communicate immediately and adjust the schedule with your approval.",
  },
  {
    headline: "Background-Checked, Experienced Crews",
    detail: "Every painter on our team is a long-term employee — not a day laborer. Our crews have worked together for years, which means consistent quality and a team that takes pride in the finished product.",
  },
  // Communication & service
  {
    headline: "Responsive Same-Day Communication",
    detail: "We answer calls and return messages the same day. You'll always know who to contact, what's happening on your project, and when the next step will occur.",
  },
  {
    headline: "Single Point of Contact",
    detail: "You deal with one person from estimate to final walk-through. No being handed off between a salesperson, a project manager, and a crew lead — just consistent, accountable service.",
  },
  {
    headline: "Hundreds of 5-Star Reviews",
    detail: "Our track record on Google, Yelp, and Houzz reflects what our clients say: exceptional quality, professional crews, and a company that does exactly what it promises.",
  },
  // Value
  {
    headline: "Competitive Pricing Without Cutting Corners",
    detail: "We're not the cheapest bid in town — and we're not the most expensive. We price fairly for the quality of materials and labor we provide, and we never cut corners to win a job.",
  },
  {
    headline: "Long-Term Value Over Low Upfront Cost",
    detail: "A paint job done right lasts 8–12 years. One done cheaply may need redoing in 3–4. We help you understand the total cost of ownership so you can make the best decision for your property.",
  },
];

/**
 * A simple, fast string hash function (djb2 variant).
 * Returns a non-negative integer.
 */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
}

/**
 * Returns 6 unique reasons for a given service+city combination:
 *   - 2–3 city-specific reasons (from CITY_REASONS)
 *   - 3–4 general Danis reasons (from GENERAL_REASONS)
 *
 * Selection is deterministic (same inputs → same output) but differs
 * across combinations, ensuring every page has unique, localized content.
 */
export function getWhyChooseReasons(
  serviceSlug: string,
  citySlug: string,
  count = 6
): WhyChooseReason[] {
  const seed = hashString(`${serviceSlug}::${citySlug}`);
  const selected: WhyChooseReason[] = [];

  // Step 1: Pick 2–3 city-specific reasons
  const cityPool = [...(CITY_REASONS[citySlug] || [])];
  const cityCount = cityPool.length >= 3 ? (seed % 2 === 0 ? 3 : 2) : cityPool.length;
  let s = seed;
  for (let i = 0; i < cityCount && cityPool.length > 0; i++) {
    s = ((s * 1664525) + 1013904223) & 0xffffffff;
    const idx = Math.abs(s) % cityPool.length;
    selected.push(cityPool[idx]);
    cityPool.splice(idx, 1);
  }

  // Step 2: Fill remaining slots with general reasons
  const generalPool = [...GENERAL_REASONS];
  const remaining = count - selected.length;
  for (let i = 0; i < remaining && generalPool.length > 0; i++) {
    s = ((s * 1664525) + 1013904223) & 0xffffffff;
    const idx = Math.abs(s) % generalPool.length;
    selected.push(generalPool[idx]);
    generalPool.splice(idx, 1);
  }

  return selected;
}
